import { commonTheme } from './common';
import { BDSColor } from '../tokens/color';
import { v2Color } from '../tokens/v2/cssVars';

/**
 * v2 alias — 리워드 웹도 라이트 단일 표면 팔레트(`affiliatePalette`)를 `--v2-*`로 주입하므로
 * 브랜드 파생 리프는 chatServiceColor와 같은 방식으로 v2 토큰을 참조한다.
 * 리워드는 다크모드가 없어 light/dark 두 값이 항상 같다.
 */
const v2 = (ref: string) => ({ light: ref, dark: ref });

export const affiliateThemeColor = {
  ...commonTheme.color,
  static: {
    white: {
      role: '',
      bubblechat: {
        light: BDSColor.common.white,
        dark: BDSColor.common.white,
      },
      tingle: {
        light: BDSColor.common.white,
        dark: BDSColor.common.white,
      },
    },
    black: {
      role: '',
      bubblechat: {
        light: BDSColor.common.black,
        dark: BDSColor.common.black,
      },
      tingle: {
        light: BDSColor.common.black,
        dark: BDSColor.common.black,
      },
    },
    // 브랜드 주색/보조색은 v2 brand 토큰에 alias(리워드 루트 V2BrandVarsGlobal이 `--v2-*` 주입).
    // 버블챗은 v2 값이 구 램프와 동일(#00E0FF / #AE85FD)이라 표시가 바뀌지 않고,
    // 팅글만 구 핑크(#FF5776)/무디블루 → 핑크(#F95388)/라임(#FFF820)으로 리스킨된다.
    primary: {
      role: '',
      bubblechat: v2(v2Color.brand.primary),
      tingle: v2(v2Color.brand.primary),
    },
    secondary: {
      role: '',
      bubblechat: v2(v2Color.brand.secondary),
      tingle: v2(v2Color.brand.secondary),
    },
    warning: {
      role: '버블챗/팅글 경고문 색상',
      bubblechat: {
        light: BDSColor.pink[50],
        dark: BDSColor.pink[50],
      },
      tingle: {
        light: BDSColor.red[55],
        dark: BDSColor.red[55],
      },
    },
    adult: {
      role: '',
      bubblechat: {
        light: BDSColor.red[50],
        dark: BDSColor.red[50],
      },
      tingle: {
        light: BDSColor.red[55],
        dark: BDSColor.red[55],
      },
    },
  },
  field: {
    background: {
      role: '최하단에 깔리는 BG 색상',
      bubblechat: {
        light: BDSColor.variant[10],
        dark: BDSColor.variant[10],
      },
      tingle: {
        light: BDSColor.variant[10],
        dark: BDSColor.variant[10],
      },
    },
    content: {
      role: '콘텐츠 필드 색상',
      bubblechat: {
        light: BDSColor.neutral[10],
        dark: BDSColor.neutral[10],
      },
      tingle: {
        light: BDSColor.neutral[10],
        dark: BDSColor.neutral[10],
      },
    },
    input: {
      role: '인풋 필드 색상',
      bubblechat: {
        light: BDSColor.variant[15],
        dark: BDSColor.variant[15],
      },
      tingle: {
        light: BDSColor.variant[15],
        dark: BDSColor.variant[15],
      },
    },
    areaChart: {
      role: '면적 차트 배경 색상',
      // 수익 차트 면적 — 선(static.primary)과 같은 브랜드색의 10% 틴트
      bubblechat: v2(v2Color.highlight.primary['10']),
      tingle: v2(v2Color.highlight.primary['10']),
    },
    toast: {
      role: '토스트, 안내문구 배경 색상',
      bubblechat: {
        light: BDSColor.variant[70],
        dark: BDSColor.variant[70],
      },
      tingle: {
        light: BDSColor.tingle_variant[70],
        dark: BDSColor.tingle_variant[70],
      },
    },
  },
  buttons: {
    default: {
      role: '버튼 default 색상',
      bubblechat: {
        light: BDSColor.variant[22],
        dark: BDSColor.variant[22],
      },
      tingle: {
        light: BDSColor.variant[22],
        dark: BDSColor.variant[22],
      },
    },
    hover: {
      role: '버튼 hover 색상',
      bubblechat: {
        light: BDSColor.neutral[10],
        dark: BDSColor.neutral[10],
      },
      tingle: {
        light: BDSColor.neutral[10],
        dark: BDSColor.neutral[10],
      },
    },
    press: {
      role: '버튼 press 색상',
      bubblechat: {
        light: BDSColor.variant[15],
        dark: BDSColor.variant[15],
      },
      tingle: {
        light: BDSColor.tingle_variant[15],
        dark: BDSColor.tingle_variant[15],
      },
    },
    disable: {
      role: '버튼 disabled 색상',
      bubblechat: {
        light: BDSColor.neutral[30],
        dark: BDSColor.cool[70],
      },
      tingle: {
        light: BDSColor.neutral[30],
        dark: BDSColor.neutral[30],
      },
    },
    sbtn_default: {
      role: '버튼 click',
      bubblechat: {
        light: BDSColor.neutral[55],
        dark: BDSColor.neutral[55],
      },
      tingle: {
        light: BDSColor.neutral[55],
        dark: BDSColor.neutral[55],
      },
    },
    sbtn_disable: {
      role: '버튼 default 2',
      bubblechat: {
        light: BDSColor.neutral[15],
        dark: BDSColor.neutral[15],
      },
      tingle: {
        light: BDSColor.neutral[15],
        dark: BDSColor.neutral[15],
      },
    },
  },
  popup: {
    background: {
      role: '팝업 bg 색상',
      bubblechat: {
        light: BDSColor.common.white,
        dark: BDSColor.cool[65],
      },
      tingle: {
        light: BDSColor.common.white,
        dark: BDSColor.warm[65],
      },
    },
    content: {
      role: '팝업 내의 콘텐츠 필드 색상',
      bubblechat: {
        light: BDSColor.variant[15],
        dark: BDSColor.cool[70],
      },
      tingle: {
        light: BDSColor.tingle_variant[15],
        dark: BDSColor.warm[70],
      },
    },
    popup: {
      role: '팝업 내의 팝업 bg 색상',
      bubblechat: {
        light: BDSColor.common.white,
        dark: BDSColor.cool[60],
      },
      tingle: {
        light: BDSColor.common.white,
        dark: BDSColor.warm[60],
      },
    },
    input: {
      role: '팝업 내의 인풋 bg 색상',
      bubblechat: {
        light: BDSColor.neutral[15],
        dark: BDSColor.cool[70],
      },
      tingle: {
        light: BDSColor.neutral[15],
        dark: BDSColor.warm[70],
      },
    },
    btn_hover: {
      role: '팝업 내의 버튼 마우스 호버 시',
      bubblechat: {
        light: BDSColor.neutral[10],
        dark: BDSColor.cool[55],
      },
      tingle: {
        light: BDSColor.neutral[10],
        dark: BDSColor.warm[55],
      },
    },
    btn_disable: {
      role: '팝업 내의 버튼 사용 불가',
      bubblechat: {
        light: BDSColor.neutral[30],
        dark: BDSColor.cool[55], // 확인 필요
      },
      tingle: {
        light: BDSColor.neutral[30],
        dark: BDSColor.warm[55], // 확인 필요
      },
    },
    btn_TXT: {
      role: '팝업 내의 버튼 텍스트 색상',
      bubblechat: {
        light: BDSColor.common.white,
        dark: BDSColor.variant[60],
      },
      tingle: {
        light: BDSColor.common.white,
        dark: BDSColor.tingle_variant[60],
      },
    },
    list_hover: {
      role: '팝업 내의 리스트 아이템 마우스 호버 시',
      bubblechat: {
        light: BDSColor.variant[10],
        dark: BDSColor.cool[55],
      },
      tingle: {
        light: BDSColor.variant[10],
        dark: BDSColor.warm[55],
      },
    },
    line_1: {
      role: '팝업 내의 라인 1',
      bubblechat: {
        light: BDSColor.variant[15],
        dark: BDSColor.cool[60],
      },
      tingle: {
        light: BDSColor.tingle_variant[15],
        dark: BDSColor.warm[60],
      },
    },
    line_2: {
      role: '팝업 내의 라인 2',
      bubblechat: {
        light: BDSColor.variant[25],
        dark: BDSColor.cool[55],
      },
      tingle: {
        light: BDSColor.tingle_variant[25],
        dark: BDSColor.warm[55],
      },
    },
    icon_2: {
      role: '팝업 내의 아이콘 2',
      bubblechat: {
        light: BDSColor.variant[40],
        dark: BDSColor.cool[45],
      },
      tingle: {
        light: BDSColor.tingle_variant[40],
        dark: BDSColor.warm[45],
      },
    },
    icon_4: {
      role: '팝업 내의 아이콘 4',
      bubblechat: {
        light: BDSColor.variant[15],
        dark: BDSColor.cool[60],
      },
      tingle: {
        light: BDSColor.tingle_variant[15],
        dark: BDSColor.warm[60],
      },
    },
    icon_5: {
      role: '팝업 내의 아이콘 5',
      bubblechat: {
        light: BDSColor.variant[60],
        dark: BDSColor.neutral[40],
      },
      tingle: {
        light: BDSColor.tingle_variant[60],
        dark: BDSColor.neutral[40],
      },
    },
    tag_default: {
      role: '팝업 내의 태그 bg 색상',
      bubblechat: {
        light: BDSColor.variant[15],
        dark: BDSColor.cool[60],
      },
      tingle: {
        light: BDSColor.tingle_variant[15],
        dark: BDSColor.warm[60],
      },
    },
    tag_edit: {
      role: '팝업 내의 태그 편집 bg 색상',
      bubblechat: {
        light: BDSColor.variant[30],
        dark: BDSColor.cool[55],
      },
      tingle: {
        light: BDSColor.tingle_variant[30],
        dark: BDSColor.warm[55],
      },
    },
  },
} as const;

// export type AffiliateBaseThemeType = typeof affiliateBaseTheme;
